import { useEffect, useState } from 'react';
import BrandMark from './BrandMark.js';
import { supabase } from './supabaseClient';
import {
  authEmailValidation,
  isAuthEmailRateLimit,
  isLikelyNetworkError,
  normalizeAuthEmail,
  RESET_REQUEST_COOLDOWN_SECONDS,
} from './authSecurity.js';
import { reportSafeDiagnostic } from './safeDiagnostics.js';
import './AuthScreen.css';

const RESET_REQUEST_MESSAGE = 'If an eligible account exists for that address, password-reset instructions will arrive shortly. For security, requests are limited; wait before trying again.';
const DEFAULT_LOGIN_BRANDING = {
  companyName:'Sign in',
  tagline:'',
  logoUrl:'',
};

function loginBrandingFields(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(['companyName','tagline','logoUrl']
    .filter(key => typeof value[key] === 'string')
    .map(key => [key,value[key]]));
}

// ── AUTH ──────────────────────────────────────────────────────────────────────
// onSignIn: called when a sign-in request starts, so the sign-in gate can
// start loading the application's code while the password is checked (FE-04).
function AuthScreen({ sessionMessage = '', onSignIn }) {
  const [mode, setMode]         = useState('login');
  const [email, setEmail]       = useState('');
  const [pass,  setPass]        = useState('');
  const [err,   setErr]         = useState('');
  const [msg,   setMsg]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const [publicBranding, setPublicBranding] = useState(null);
  const resetCoolingDown = resetCooldown > 0;
  const loginBranding = {
    ...DEFAULT_LOGIN_BRANDING,
    ...loginBrandingFields(publicBranding),
  };
  const companyName = loginBranding.companyName.trim() || DEFAULT_LOGIN_BRANDING.companyName;
  const tagline = loginBranding.tagline.trim();

  useEffect(() => {
    let current = true;
    // Only these three public identity strings are read before authentication.
    // The full workspace configuration remains behind the signed-in boundary.
    Promise.resolve().then(() => supabase.rpc('get_login_branding_v1'))
      .then(({ data, error }) => { if (current && !error) setPublicBranding(data); })
      .catch(() => {});
    return () => { current = false; };
  }, []);

  useEffect(() => { document.title = companyName === DEFAULT_LOGIN_BRANDING.companyName ? 'Sign in' : `${companyName} · Sign in`; }, [companyName]);

  useEffect(() => {
    if (!resetCoolingDown) return undefined;
    const timer = window.setInterval(() => {
      setResetCooldown(current => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resetCoolingDown]);

  async function submit(e) {
    e.preventDefault();
    if (loading || (mode === 'reset' && resetCooldown > 0)) return;
    setErr('');
    setMsg('');

    const emailError = authEmailValidation(email);
    if (emailError) {
      setErr(emailError);
      return;
    }
    const normalizedEmail = normalizeAuthEmail(email);
    setLoading(true);

    try {
      if (mode === 'reset') {
        const redirectUrl = new URL('/', window.location.origin);
        redirectUrl.searchParams.set('auth_flow', 'recovery');
        const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
          redirectTo: redirectUrl.toString(),
        });
        if (error) {
          reportSafeDiagnostic('warn', 'password_reset_request_not_delivered', {
            stage:'request_password_reset',
            retryable:isLikelyNetworkError(error) || isAuthEmailRateLimit(error),
          }, error);
          if (isLikelyNetworkError(error)) {
            setErr('The authentication service could not be reached. Check your connection and try again.');
            return;
          }
        }
        // Supabase deliberately does not establish whether an address exists.
        // Keep rate-limit and unknown-account responses indistinguishable too.
        setMsg(RESET_REQUEST_MESSAGE);
        setResetCooldown(RESET_REQUEST_COOLDOWN_SECONDS);
        return;
      }

      onSignIn?.();
      const { error } = await supabase.auth.signInWithPassword({ email:normalizedEmail, password:pass });
      if (error) {
        reportSafeDiagnostic('warn', 'password_sign_in_failed', {
          stage:'password_sign_in',
          retryable:isLikelyNetworkError(error),
        }, error);
        setErr(isLikelyNetworkError(error)
          ? 'The authentication service could not be reached. Check your connection and try again.'
          : 'Email or password is incorrect, or this account cannot sign in.');
      }
    } catch (error) {
      reportSafeDiagnostic('warn', mode === 'reset' ? 'password_reset_request_failed' : 'password_sign_in_failed', {
        stage:mode === 'reset' ? 'request_password_reset' : 'password_sign_in',
        retryable:true,
      }, error);
      setErr('The authentication service could not be reached. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  const IS2 = { background:'var(--surface3)', border:'1.5px solid var(--border)', borderRadius:'var(--radius)', color:'var(--text)', fontFamily:'var(--mono)', fontSize:14, padding:'9px 12px', outline:'2px solid transparent', outlineOffset:2, width:'100%', transition:'all .2s' };

  return (
    <main className="auth-screen">
      <div className="auth-screen__card">
        <div className="auth-screen__header">
          <div className="auth-screen__mark"><BrandMark branding={loginBranding} size={52}/></div>
          <h1 className="auth-screen__title" style={{overflowWrap:'anywhere'}}>{companyName}</h1>
          {tagline && <div className="auth-screen__tagline" style={{overflowWrap:'anywhere'}}>{tagline}</div>}

        </div>

        {mode === 'reset' && (
          <div className="auth-screen__reset-header">
            <button type="button" disabled={loading} onClick={() => { setMode('login'); setErr(''); setMsg(''); }}
              className="auth-screen__back">Back</button>
            <span className="auth-screen__reset-title">Reset Password</span>
          </div>
        )}

        <form onSubmit={submit} className="auth-screen__form">
          {sessionMessage && mode === 'login' && <div role="status" aria-live="polite" className="auth-screen__notice">{sessionMessage}</div>}
          <div>
            <label className="auth-screen__label" htmlFor="auth-email">EMAIL</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} id="auth-email" name="email" autoComplete="email" inputMode="email" maxLength={254} placeholder="your@email.com" required disabled={loading} style={IS2}/>
          </div>
          {mode !== 'reset' && (
            <div>
              <label className="auth-screen__label" htmlFor="auth-password">PASSWORD</label>
              <input id="auth-password" name="password" type="password" autoComplete="current-password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" required disabled={loading} style={IS2}/>
            </div>
          )}
          {err && <div role="alert" className="auth-screen__error">{err}</div>}
          {msg && <div role="status" aria-live="polite" className="auth-screen__message">{msg}</div>}
          <button type="submit" disabled={loading || (mode === 'reset' && resetCooldown > 0)} aria-busy={loading}
            style={{ background:'var(--selected-bg)', border:'1px solid var(--selected-border)', borderRadius:9, color:'var(--info)', fontSize:14, fontWeight:700, padding:'11px', cursor:loading || resetCooldown > 0?'not-allowed':'pointer', opacity:loading || resetCooldown > 0?0.6:1, marginTop:4, boxShadow:'none', transition:'all .2s' }}>
            {loading
              ? 'Please wait...'
              : mode === 'login'
                ? 'Sign In'
                : resetCooldown > 0
                  ? `Try again in ${resetCooldown}s`
                  : 'Send Reset Email'}
          </button>
          {mode === 'login' && (
            <button type="button" disabled={loading} onClick={() => { setMode('reset'); setErr(''); setMsg(''); }}
              className="auth-screen__forgot">
              Forgot password?
            </button>
          )}
        </form>
        <div className="auth-screen__footnote">
          Access is invitation-only. Contact an administrator if you need an account.
        </div>
      </div>
    </main>
  );
}

export default AuthScreen;
